// Chemin: C:\gestion-copro\src\pages\public\PolitiqueConfidentialitePage.tsx
// Page Politique de Confidentialité - Conformité RGPD
// Session 78 : Création initiale

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Shield,
  Database,
  Clock,
  UserCheck,
  Share2,
  Lock,
  Mail,
  Server,
  FileText,
  Eye,
  Trash2
} from 'lucide-react';

const PolitiqueConfidentialitePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header simple */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
              Retour à l'accueil
            </Link>
            <img 
              src="/logo-copro-protect.png" 
              alt="Copro Protect" 
              className="h-10 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Contenu */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Politique de confidentialité</h1>
        <p className="text-gray-500 mb-8">Dernière mise à jour : 3 février 2026</p>

        {/* Encadré RGPD */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-blue-900 mb-2">Notre engagement RGPD</h2>
              <p className="text-blue-800 text-sm">
                DOM TECH & SERVICES s'engage à protéger la vie privée des utilisateurs de Copro Protect 
                conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi 
                Informatique et Libertés. Vos données sont hébergées exclusivement en France.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-200">
          
          {/* Section 1 : Responsable du traitement */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">1. Responsable du traitement</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>Le responsable du traitement des données personnelles est :</p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-semibold text-gray-900">DOM TECH & SERVICES</p>
                <p>155 rue des Coccinelles — 62180 Rang-du-Fliers, France</p>
                <p>RCS Boulogne-sur-Mer 939 094 231</p>
                <p className="mt-2">
                  <strong>Contact DPO :</strong>{' '}
                  <a href="mailto:dominique.sergent62@gmail.com" className="text-blue-600 hover:underline">
                    dominique.sergent62@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 : Données collectées */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">2. Données collectées</h2>
            </div>
            
            <div className="space-y-4 text-gray-600">
              <p>Nous collectons les catégories de données suivantes :</p>
              
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Données d'identification</h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Nom et prénom</li>
                    <li>Adresse email</li>
                    <li>Numéro de téléphone (optionnel)</li>
                    <li>Adresse postale (pour les copropriétaires)</li>
                    <li>Numéro de lot en copropriété</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Données de connexion</h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Identifiant de compte</li>
                    <li>Mot de passe (chiffré)</li>
                    <li>Historique de connexion (date, heure, adresse IP)</li>
                    <li>Type d'appareil et navigateur</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Données d'utilisation</h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Documents uploadés (PV, courriers, factures, photos)</li>
                    <li>Signalements créés</li>
                    <li>Messages et courriers rédigés</li>
                    <li>Interactions avec l'assistant SENTINELLE</li>
                    <li>Statistiques d'utilisation des modules</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 : Finalités du traitement */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">3. Finalités du traitement</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>Vos données sont traitées pour les finalités suivantes :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Fourniture du service</strong> : gestion de votre compte, accès aux 
                  fonctionnalités de la plateforme
                </li>
                <li>
                  <strong>Gestion de la copropriété</strong> : suivi des signalements, gestion 
                  documentaire, communication avec les interlocuteurs
                </li>
                <li>
                  <strong>Intelligence artificielle</strong> : analyse de documents, génération 
                  de courriers, assistance via SENTINELLE
                </li>
                <li>
                  <strong>Amélioration du service</strong> : statistiques d'utilisation anonymisées, 
                  détection et correction des bugs
                </li>
                <li>
                  <strong>Communication</strong> : envoi des identifiants, notifications importantes, 
                  alertes (GPA, quotas)
                </li>
                <li>
                  <strong>Obligations légales</strong> : respect des obligations comptables et fiscales
                </li>
              </ul>
            </div>
          </section>

          {/* Section 4 : Base légale */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">4. Base légale du traitement</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>Le traitement de vos données repose sur les bases légales suivantes :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Exécution du contrat</strong> (article 6.1.b du RGPD) : le traitement est 
                  nécessaire à l'exécution du contrat d'abonnement
                </li>
                <li>
                  <strong>Intérêt légitime</strong> (article 6.1.f du RGPD) : amélioration du service, 
                  sécurité de la plateforme
                </li>
                <li>
                  <strong>Obligation légale</strong> (article 6.1.c du RGPD) : conservation des données 
                  de facturation
                </li>
              </ul>
            </div>
          </section>

          {/* Section 5 : Durée de conservation */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">5. Durée de conservation</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>Vos données sont conservées selon les durées suivantes :</p>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold">Type de données</th>
                      <th className="text-left py-2 font-semibold">Durée de conservation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Données de compte</td>
                      <td className="py-2">Durée de l'abonnement + 30 jours</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Documents et signalements</td>
                      <td className="py-2">Durée de l'abonnement + 30 jours</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Logs de connexion</td>
                      <td className="py-2">12 mois</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Données de facturation</td>
                      <td className="py-2">10 ans (obligation légale)</td>
                    </tr>
                    <tr>
                      <td className="py-2">Conversations SENTINELLE</td>
                      <td className="py-2">Durée de l'abonnement</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p className="text-sm">
                À l'issue de ces délais, les données sont supprimées ou anonymisées de manière irréversible.
              </p>
            </div>
          </section>

          {/* Section 6 : Destinataires des données */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Share2 className="w-5 h-5 text-teal-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">6. Destinataires des données</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>Vos données peuvent être transmises aux destinataires suivants :</p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Au sein de la copropriété</strong> : les membres du conseil syndical et 
                  l'administrateur ont accès aux données de la copropriété selon leurs permissions
                </li>
                <li>
                  <strong>Sous-traitants techniques</strong> :
                  <ul className="list-circle pl-6 mt-1 space-y-1">
                    <li>Google Cloud / Firebase (hébergement — France)</li>
                    <li>Anthropic (API Claude pour SENTINELLE — données non conservées)</li>
                  </ul>
                </li>
                <li>
                  <strong>Autorités</strong> : en cas d'obligation légale ou de réquisition judiciaire
                </li>
              </ul>
              
              <p className="mt-3">
                <strong>Nous ne vendons jamais vos données</strong> à des tiers à des fins commerciales 
                ou publicitaires.
              </p>
            </div>
          </section>

          {/* Section 7 : Hébergement et sécurité */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Server className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">7. Hébergement et sécurité</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800 font-medium">
                  🇫🇷 Vos données sont hébergées en France
                </p>
                <p className="text-green-700 text-sm mt-1">
                  Serveurs Google Cloud situés à Paris (région europe-west9)
                </p>
              </div>
              
              <p><strong>Mesures de sécurité mises en œuvre :</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Chiffrement des données en transit (HTTPS/TLS)</li>
                <li>Chiffrement des données au repos</li>
                <li>Authentification sécurisée (Firebase Authentication)</li>
                <li>Mots de passe hashés (non stockés en clair)</li>
                <li>Contrôle d'accès basé sur les rôles (RBAC)</li>
                <li>Journalisation des accès et actions sensibles</li>
                <li>Sauvegardes régulières</li>
              </ul>
            </div>
          </section>

          {/* Section 8 : Vos droits */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">8. Vos droits</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>Conformément au RGPD, vous disposez des droits suivants :</p>
              
              <div className="grid sm:grid-cols-2 gap-4 my-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">Droit d'accès</h3>
                  <p className="text-sm">Obtenir une copie de vos données personnelles</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">Droit de rectification</h3>
                  <p className="text-sm">Corriger vos données inexactes ou incomplètes</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">Droit à l'effacement</h3>
                  <p className="text-sm">Demander la suppression de vos données</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">Droit à la portabilité</h3>
                  <p className="text-sm">Récupérer vos données dans un format standard</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">Droit d'opposition</h3>
                  <p className="text-sm">Vous opposer à certains traitements</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">Droit à la limitation</h3>
                  <p className="text-sm">Limiter temporairement le traitement</p>
                </div>
              </div>
              
              <p>
                Pour exercer ces droits, contactez-nous à :{' '}
                <a href="mailto:dominique.sergent62@gmail.com" className="text-blue-600 hover:underline">
                  dominique.sergent62@gmail.com
                </a>
              </p>
              <p>
                Nous répondrons à votre demande dans un délai maximum de 30 jours.
              </p>
              <p className="mt-3">
                Vous disposez également du droit d'introduire une réclamation auprès de la CNIL :{' '}
                <a 
                  href="https://www.cnil.fr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  www.cnil.fr
                </a>
              </p>
            </div>
          </section>

          {/* Section 9 : Cookies */}
          <section className="p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Cookies</h2>
            
            <div className="space-y-3 text-gray-600">
              <p>
                Copro Protect utilise uniquement des cookies techniques strictement nécessaires au 
                fonctionnement de la plateforme :
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Cookie d'authentification (session utilisateur)</li>
                <li>Cookie de préférences (fermeture du bandeau mobile)</li>
              </ul>
              <p>
                Nous n'utilisons pas de cookies publicitaires ni de trackers tiers. Aucun 
                consentement n'est donc requis pour ces cookies essentiels.
              </p>
            </div>
          </section>

          {/* Section 10 : Modifications */}
          <section className="p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Modifications de cette politique</h2>
            
            <div className="space-y-3 text-gray-600">
              <p>
                Cette politique de confidentialité peut être mise à jour pour refléter les évolutions 
                de nos pratiques ou de la réglementation. En cas de modification substantielle, nous 
                vous en informerons par email ou via une notification dans l'application.
              </p>
              <p>
                La date de dernière mise à jour est indiquée en haut de cette page.
              </p>
            </div>
          </section>

          {/* Section 11 : Contact */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">11. Contact</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>
                Pour toute question concernant cette politique de confidentialité ou le traitement 
                de vos données personnelles :
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-semibold text-gray-900">DOM TECH & SERVICES</p>
                <p>Délégué à la Protection des Données</p>
                <p className="mt-2">
                  <a href="mailto:dominique.sergent62@gmail.com" className="text-blue-600 hover:underline">
                    dominique.sergent62@gmail.com
                  </a>
                </p>
                <p>
                  <a href="tel:+33673763095" className="text-blue-600 hover:underline">
                    06 73 76 30 95
                  </a>
                </p>
              </div>
            </div>
          </section>

        </div>

        {/* Liens vers autres pages légales */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/mentions-legales" 
            className="text-blue-600 hover:underline"
          >
            Mentions légales
          </Link>
          <span className="text-gray-300">|</span>
          <Link 
            to="/cgu" 
            className="text-blue-600 hover:underline"
          >
            Conditions générales d'utilisation
          </Link>
        </div>
      </main>

      {/* Footer simple */}
      <footer className="bg-gray-900 text-gray-400 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm">
          © 2026 DOM TECH & SERVICES — Tous droits réservés
        </div>
      </footer>
    </div>
  );
};

export default PolitiqueConfidentialitePage;
